import { Box, Flex, Text } from '@chakra-ui/react';
import { FunctionComponent, useState } from 'react';
import { StepPageLayout } from '@/layouts/StepPageLayout';
import { setGeneratedMeta, setProcessedMeta, useAppDispatch, useAppSelector } from '@/redux';
import { DashboardMeta, GeneratedMeta } from '@/types/metaProcessing';
import { MetaEditorPanel } from './components/MetaEditorPanel';
import { MetaProgressPanel } from './components/MetaProgressPanel';
import { META_PROCESSING_STEP } from './constants';

// Both sides are formatted the same way so the diff highlights only real changes,
// not differences in how the stored file happens to be indented.
const formatMeta = (meta: DashboardMeta) => JSON.stringify(meta, null, 2);

export const MetaProcessingPage: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  // In Redux, not local state, so the Publish step uploads exactly what was reviewed here
  // (including edits) and coming Back restores the comparison.
  const originalMeta = useAppSelector((state) => state.wave.originalMeta);
  const processedMeta = useAppSelector((state) => state.wave.processedMeta);
  const [isReviewed, setIsReviewed] = useState(false);

  const hasMeta = processedMeta.trim().length > 0;

  const handleGenerated = ({ current, processed }: GeneratedMeta) => {
    dispatch(setGeneratedMeta({ originalMeta: formatMeta(current), processedMeta: formatMeta(processed) }));
    // A fresh result hasn't been reviewed yet.
    setIsReviewed(false);
  };

  const handleMetaChange = (next: string) => {
    dispatch(setProcessedMeta(next));
    // An edit after review means the reviewed version is no longer the one shown.
    setIsReviewed(false);
  };

  if (!selectedClient) {
    return (
      <Box p={8}>
        <Text color="marketcast.text.primary">Client not found</Text>
      </Box>
    );
  }

  return (
    <StepPageLayout
      currentStep={META_PROCESSING_STEP}
      isNextEnabled={hasMeta && isReviewed}
      nextDisabledReason="Review the processed meta and confirm to continue"
    >
      {/* Large screens: 2/3 editor, 1/3 progress; the editor fills the height. */}
      <Flex
        h="100%"
        gap={6}
        p={6}
        direction={{ base: 'column', lg: 'row' }}
        overflowY={{ base: 'auto', lg: 'hidden' }}
      >
        <Box flex={{ lg: 2 }} minW={0} h={{ lg: '100%' }}>
          <MetaEditorPanel
            originalMeta={originalMeta}
            meta={processedMeta}
            onChange={handleMetaChange}
          />
        </Box>
        <Box flex={{ lg: 1 }} minW={0} overflowY={{ lg: 'auto' }}>
          <MetaProgressPanel
            hasMeta={hasMeta}
            isReviewed={isReviewed}
            onReviewedChange={setIsReviewed}
            onGenerated={handleGenerated}
          />
        </Box>
      </Flex>
    </StepPageLayout>
  );
};
